import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { getArticleBySlug } from '@/mocks/articles';
import type { ArticleData } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://sugargoospreadsheet.icu';

const labelColorMap: Record<string, string> = {
  accent: 'text-accent-700 bg-accent-100',
  secondary: 'text-secondary-700 bg-secondary-100',
  primary: 'text-primary-700 bg-primary-100',
};

function buildArticleSEO(article: ArticleData) {
  return {
    title: `${article.title} | Sugargoo Spreadsheet Guide`,
    description: article.description,
    keywords: `sugargoo spreadsheet, ${article.label.toLowerCase()}, sugargoo guide, sugargoo ${article.label.toLowerCase()}`,
    canonicalUrl: `${SITE_URL}/articles/${article.slug}`,
    ogTitle: `${article.title} | Sugargoo Spreadsheet Guide`,
    ogDescription: article.description,
    ogType: 'article' as const,
    ogUrl: `${SITE_URL}/articles/${article.slug}`,
    ogImage: article.imageUrl,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      image: article.imageUrl,
      author: {
        '@type': 'Organization',
        name: 'Sugargoo Spreadsheet Guide',
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Sugargoo Spreadsheet Guide',
        url: SITE_URL,
      },
      datePublished: '2026-06-01',
      dateModified: '2026-06-17',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/articles/${article.slug}`,
      },
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <Header />
        <main>
          <section className="pt-32 pb-20 bg-background-50 text-center">
            <div className="max-w-2xl mx-auto px-4">
              <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
              <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
              <Link to="/" className="inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm"></i></span>
                Back to Homepage
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const labelClass = labelColorMap[article.labelColor] || labelColorMap.secondary;

  return (
    <>
      <SEOHead {...buildArticleSEO(article)} />
      <Header />
      <main>
        <article>
          <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
                <div className="w-full lg:w-2/5 lg:sticky lg:top-24">
                  <div className="rounded-2xl overflow-hidden aspect-[4/5] bg-accent-100/50">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-6 bg-background-100 rounded-xl p-5">
                    <h4 className="font-heading text-base font-bold text-foreground-950 mb-3">Table of Contents</h4>
                    <nav className="space-y-2 text-sm">
                      {article.sections.map((section, idx) => (
                        <a
                          key={section.id}
                          href={`#${section.id}`}
                          className="block text-foreground-600 hover:text-primary-600 transition-colors"
                        >
                          {idx + 1}. {section.heading}
                        </a>
                      ))}
                    </nav>
                  </div>
                </div>
                <div className="w-full lg:w-3/5">
                  <span className={`text-xs font-semibold tracking-wider px-2.5 py-1 rounded-full ${labelClass}`}>
                    {article.label}
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mt-3 mb-8 leading-tight">
                    {article.title}
                  </h1>
                  <div className="space-y-8 text-foreground-700 leading-relaxed">
                    {article.sections.map((section) => (
                      <div key={section.id} id={section.id}>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 pt-2 pb-4">
                          {section.heading}
                        </h2>
                        {section.paragraphs.map((para, i) => (
                          <p
                            key={i}
                            className="mb-4 text-sm md:text-base"
                            dangerouslySetInnerHTML={{ __html: para }}
                          />
                        ))}
                        {section.listItems && section.listItems.length > 0 && (
                          <ul className="list-disc pl-5 space-y-3 mt-4 mb-6">
                            {section.listItems.map((item, i) => (
                              <li key={i} className="text-sm md:text-base">{item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                    <p className="text-base md:text-lg font-medium text-foreground-950 pt-4">
                      Ready to start exploring? Head back to the{' '}
                      <Link to="/" className="text-primary-600 font-semibold hover:text-primary-700 underline">
                        homepage
                      </Link>{' '}
                      and dive into our complete collection of Sugargoo spreadsheet categories. Happy shopping!
                    </p>
                  </div>
                  <div className="mt-10 pt-8 border-t border-background-200">
                    <p className="text-sm text-foreground-500 mb-2">
                      &mdash; Sugargoo Spreadsheet Team
                    </p>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-line text-sm"></i>
                      </span>
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}